import React from 'react';
import type { MoFamily } from '../pages/mofamily/types';
import { FamilyAvatar } from './FamilyAvatar';
import { useAppStore } from '../App';

/**
 * "소유주: A · 운영: B" 한 줄 — 색 띠·배지만으로는 "내가 운영하는지, 파트너가 운영하는지"를 알아보는
 * 데 시간이 걸린다는 피드백(2026-10-21)으로, 두 역할을 글자로 못 박는다. 나 자신은 "나"로.
 */
export const OwnerOperatorLine: React.FC<{ family: MoFamily; className?: string }> = ({ family, className }) => {
 const me = useAppStore(s => s.user?.moPeerHash);
 const label = (hash: string | null | undefined, name: string | null | undefined) =>
  hash && me && hash === me ? '나' : (name || '알 수 없음');
 const owner = label(family.ownerMoPeerHash, family.ownerName);
 const ops = (family.operators ?? []);
 // 나를 맨 앞에, 나머지는 이름순.
 const names = [
  ...ops.filter(o => o.moPeerHash === me).map(() => '나'),
  ...ops.filter(o => o.moPeerHash !== me).map(o => o.name).sort(),
 ];
 const operatorText = names.length === 0 ? '없음 (슬롯 없음)' : names.join(', ');
 const mineOnly = names.length > 0 && names.every(n => n === '나');
 return (
  <div className={`text-xs flex flex-wrap gap-x-3 gap-y-0.5 ${className ?? ''}`}>
   <span className="text-gray-600"><span className="text-gray-400">소유주</span> {owner}</span>
   <span className={mineOnly ? 'text-gray-600' : 'text-indigo-700 font-medium'}>
    <span className="text-gray-400 font-normal">운영</span> {operatorText}
   </span>
  </div>
 );
};

/** 계층 위치 아이콘 — 세로 3점을 선으로 연결하고, 해당 위치 점만 강조(파랑·크게). */
export const HierarchyIcon: React.FC<{ pos: 'top' | 'middle' | 'bottom'; title?: string; className?: string }> = ({ pos, title, className }) => {
 const cy = { top: 4, middle: 14, bottom: 24 } as const;
 return (
  <svg width="8" height="22" viewBox="0 0 10 28" className={className} role="img" aria-label={title}>
   {title ? <title>{title}</title> : null}
   <line x1="5" y1="4" x2="5" y2="24" stroke="#cbd5e1" strokeWidth="1.5" />
   {(['top', 'middle', 'bottom'] as const).map((d) => (
    <circle key={d} cx="5" cy={cy[d]} r={d === pos ? 3.6 : 1.8} fill={d === pos ? '#4f46e5' : '#cbd5e1'} />
   ))}
  </svg>
 );
};

export const FamilyCard: React.FC<{
 family: MoFamily;
 onDetail: () => void;
}> = ({ family, onDetail }) => {
 const isDefault = family.isDefault ?? (
  family.description?.includes('자동 생성') ||
  family.description?.includes('기본 모패밀리') ||
  family.name?.includes('기본 모패밀리') ||
  family.maxMembers === 1
 );

 const handleClick = () => {
 console.log('FamilyCard clicked:', family.moFamilyHash);
 onDetail();
 };

 // 상세 페이지(HierarchyDisplay)와 동일 원칙: 슬롯은 있으나 내가 직접 채울 수 있는 슬롯이
 // 하나도 없으면(전체 위임/바터) 사선 배경. 서버가 내려준 hasControllableSlot 으로 판정.
 // (과거엔 ownedSlots===0 에 의존했으나 my-families API 가 ownedSlots 를 안 내려줘 항상 false 였음)
 const hasNoRemainingSlots = (family.totalSlots ?? 0) > 0 && family.hasControllableSlot === false;
 // 슬롯 단위 'slot-no-authority' 와 같은 블루그레이 톤 (사선 = 제어 불가 일관화).
 const noControlDiagonal = 'repeating-linear-gradient(45deg, rgba(96,125,139,0.14) 0, rgba(96,125,139,0.14) 5px, transparent 5px, transparent 12px)';

 const isBarter = family.familyType === 'barter';
 const barterPaired = isBarter && !!family.barterPairId;

 // 관계 라벨 — 비-소유(합류/위임) 패밀리만 배지로 구분.
 // 목록(my-families)은 현재 코드상 항상 내가 소유한 패밀리만 반환하므로 "내 소유" 배지는
 // 모든 카드에 붙어 정보량이 없어 생략. (레거시/향후 비-owner 패밀리가 끼면 그건 아래 배지로 구분됨)
 const isOwner = family.isOwner || family.role === 'owner';
 const roleLabel = isOwner
  ? null
  : family.role === 'operator'
  ? { text: '운영자 합류', cls: 'bg-amber-50 text-amber-800 border border-amber-200' }
  : family.role === 'member'
  ? { text: '멤버', cls: 'bg-gray-100 text-gray-700 border border-gray-200' }
  : family.role === 'relationship' && family.relationType === 'parent'
  ? { text: '상위 패밀리', cls: 'bg-emerald-50 text-emerald-800 border border-emerald-200' }
  : family.role === 'relationship' && family.relationType === 'child'
  ? { text: '하위 패밀리', cls: 'bg-teal-50 text-teal-800 border border-teal-200' }
  : null;

 // 용도/가용 상태 — "여기 영상 넣을 수 있나?" 를 한눈에. role 라벨(소유 관계)과 보완.
 // 우선순위: 바터 > 슬롯없음 > 전체위임 > 넣기가능.
 const totalSlots = family.totalSlots ?? 0;
 const capability = isBarter
  ? { text: '바터용', cls: 'bg-purple-100 text-purple-800 border border-purple-200', strip: 'border-l-purple-400', bg: 'bg-purple-50', title: '슬롯 교환(바터) 전용 모패밀리' }
  : totalSlots === 0
  ? { text: '슬롯 없음', cls: 'bg-gray-100 text-gray-600 border border-gray-200', strip: 'border-l-gray-300', bg: 'bg-gray-50', title: '자체 슬롯이 없는 모패밀리 (위임·광고주용)' }
  : hasNoRemainingSlots
  ? { text: '전체 위임됨', cls: 'bg-orange-100 text-orange-800 border border-orange-200', strip: 'border-l-orange-400', bg: 'bg-orange-50', title: '모든 슬롯을 위임해 영상을 넣을 수 없음' }
  : { text: '영상 넣기 가능', cls: 'bg-green-100 text-green-800 border border-green-200', strip: 'border-l-green-500', bg: 'bg-green-50', title: '내가 영상을 넣을 수 있는 슬롯이 있는 모패밀리' };

 // 좌측 컬러 strip — 카드 왼쪽 가장자리만 훑어도 용도가 구분된다.
 // 바터 체결은 진한 보라 우선(짝꿍과 한 쌍임 강조), 그 외엔 가용 상태 색.
 const stripClass = barterPaired ? 'border-l-4 border-l-purple-500' : `border-l-4 ${capability.strip}`;
 // 카드 배경 — strip 과 같은 톤의 더 연한 색으로 채운다.
 const bgClass = barterPaired ? 'bg-purple-50' : capability.bg;

 // 계층 위치 — 슬롯 위임 트리에서 이 패밀리의 자리.
 // childCount = 나에게 위임한 하위(내가 부모), parentCount = 내가 위임한 상위(내가 자식).
 const hasChildren = (family.childCount ?? 0) > 0;
 const hasParent = (family.parentCount ?? 0) > 0;
 const hierarchyPos = hasChildren && hasParent
  ? { pos: 'middle' as const, title: '중간 계층 — 위·아래 모두 연결' }
  : hasChildren
  ? { pos: 'top' as const, title: '최상위(루트) — 위임을 받기만 함' }
  : hasParent
  ? { pos: 'bottom' as const, title: '최하위(막내) — 상위에만 위임' }
  : null; // 관계 없음(단독) — 계층 아이콘 없음

 return (
 <div
 className={`p-6 cursor-pointer ${bgClass} border border-gray-200 ${stripClass}`}
 style={hasNoRemainingSlots ? { backgroundImage: noControlDiagonal } : undefined}
 title={hasNoRemainingSlots ? '내가 제어할 수 있는 슬롯이 없는 모패밀리입니다' : undefined}
 onClick={handleClick}>
 <div className="flex items-start gap-3 mb-4">
 <FamilyAvatar name={family.name} familyHash={family.moFamilyHash} imageUrl={family.imageUrl} size={48} />
 <div className="flex-1 min-w-0">
 <h3 className="text-lg font-semibold flex items-center gap-2 flex-wrap">
 {hierarchyPos && <HierarchyIcon pos={hierarchyPos.pos} title={hierarchyPos.title} className="align-middle shrink-0" />}
 <span>{family.name}</span>
 {isDefault && <span className="text-sm text-gray-500 font-normal">(개인 전용)</span>}
 {roleLabel && (
 <span className={`text-xs px-1.5 py-0.5 font-normal ${roleLabel.cls}`}>{roleLabel.text}</span>
 )}
 <span className={`text-xs px-1.5 py-0.5 font-normal ${capability.cls}`} title={capability.title}>{capability.text}</span>
 {isBarter && (
 <span
  className={`text-base ${barterPaired ? 'text-purple-700' : 'text-purple-400'}`}
  title={barterPaired ? '바터 (체결됨)' : '바터 (대기 중)'}
 >
  🔄
 </span>
 )}
 </h3>
 {/* 소유주·운영자를 글자로 — 색 띠/배지의 뜻을 몰라도 "누구 것이고 누가 영상을 넣는지" 바로 읽힌다. */}
 <OwnerOperatorLine family={family} className="mt-1" />
 {family.description && (
 <p className="text-gray-600 text-sm mt-1">{family.description}</p>
 )}
 {barterPaired && family.pairFamilyName && (
 <p className="text-xs text-purple-700 mt-1 truncate" title={family.pairFamilyName}>
  ↔ {family.pairFamilyName}
 </p>
 )}
 </div>
 </div>

 <div className="text-sm text-gray-500">
  <p>하위 모패밀리: {family.childCount ?? family.collaboratingCount ?? 0}개</p>
  <p>슬롯: {family.totalSlots || 0}개 {hasNoRemainingSlots && <span className="text-xs text-orange-600">(전체 위임됨)</span>}</p>
 </div>
 </div>
 );
};
